import _ from 'underscore';
import type { I18nLike } from '@bingads-webui/common-types';
import {
  PaymentInstrFinancialStatus,
  CardCategory,
  CountryCode,
  CreditCardType,
  PaymentInstrumentType,
  PaymentInstrLifecycleStatus,
  PaymentType,
  PaymentTypeId,
  PaymentOption,
  PaymentOptionId,
  PaymentSettingsMap,
  PaymentTransactionStatus,
  SAPApplicationType,
  SAPApplicationStatus,
  DocumentType,
  AccountMode,
  ESCPrepayUnsupportedPITypes,
  NukedPIStatusTypes,
  CreditCardTypeString,
  PaymentOptionString,
} from './constants';
import type { PaymentInstrument } from './types';

type PaymentI18n = Pick<I18nLike, 'getString'> & Partial<Pick<I18nLike, 'formatCurrency'>> & {
  formatDate?: (date: Date, options?: Intl.DateTimeFormatOptions & { format?: string }) => string;
};

interface SAPApplication {
  SAPApplicationType?: number;
  SAPApplicationStatus?: number;
}

interface PaymentMethodInformation {
  paymentInstrumentTypeId?: number;
  paymentInstrAccountNumber?: string;
  paymentInstrFriendlyName?: string;
  paymentInstrCardTypeId?: number;
}

interface PaymentPilots {
  UnifiedProductMonthlyInvoice?: boolean;
  ESCPrepay?: boolean;
}

const PaymentStatusI18nMap = {
  [PaymentTransactionStatus.Processing]: _TL_('Payment processing'),
  [PaymentTransactionStatus.Declined]: _TL_('Payment declined'),
  [PaymentTransactionStatus.Chargeback]: _TL_('Chargeback'),
  [PaymentTransactionStatus.RefundReversal]: _TL_('Refund reversal'),
  [PaymentTransactionStatus.ChargebackReversal]: _TL_('Chargeback reversal'),
};

export const getCardNumberLast4Digits = (number?: string) => _.isString(number) ? number.substr(-4) : '';

const getCreditCardDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) => {
  const displayNameKey = pi.Address ? _TL_('{{cardType}} {{cardCategory}}****{{last4}} ({{country}})') : _TL_('{{cardType}} {{cardCategory}}****{{last4}}');
  const cardTypeKey = pi.CardTypeId
    ? CreditCardTypeString[pi.CardTypeId as keyof typeof CreditCardTypeString]
    : CreditCardTypeString[CreditCardType[pi.CardType as keyof typeof CreditCardType] as keyof typeof CreditCardTypeString];

  return i18n.getString(
    displayNameKey,
    {
      cardType: i18n.getString(cardTypeKey),
      cardCategory: pi.CardCategory === CardCategory.Prepaid ? i18n.getString(_TL_('Prepaid ')) : '',
      last4: getCardNumberLast4Digits(pi.Number),
      country: pi.Address ? pi.Address.CountryName : '',
    }
  );
};

const getInvoiceDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) => {
  const stringFormat = pi.SAPCustomerName ? _TL_('SAP: {{customerName}} ({{invoiceId}})') : _TL_('SAP: {{invoiceId}}');
  return i18n.getString(
    stringFormat,
    {
      customerName: pi.SAPCustomerName,
      invoiceId: pi.InvoiceId,
    }
  );
};

const getCheckDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  i18n.getString(_TL_('Check: {{friendlyName}}'), { friendlyName: pi.FriendlyName });

const getEFTDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  i18n.getString(_TL_('Electronic funds transfer: {{friendlyName}}'), { friendlyName: pi.FriendlyName });

const getPayPalDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  (pi.Email ? i18n.getString(_TL_('PayPal: {{email}}'), { email: pi.Email }) : i18n.getString(_TL_('PayPal')));

const getSepaDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  i18n.getString(_TL_('SEPA direct debit: {{friendlyName}}'), { friendlyName: pi.FriendlyName });

const getOfflineDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) => {
  const stringFormat = pi.Address ? _TL_('Bank transfer ({{country}})') : _TL_('Bank transfer');
  return i18n.getString(stringFormat, { country: pi.Address ? pi.Address.CountryName : '' });
};

const getBoletoDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  i18n.getString(_TL_('Boleto Bancário: {{friendlyName}}'), { friendlyName: pi.FriendlyName });

const getVBADisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) =>
  i18n.getString(_TL_('Virtual Bank Account: {{friendlyName}}'), { friendlyName: pi.FriendlyName });

const getDisplayNameDict = {
  [PaymentInstrumentType.CreditCard]: getCreditCardDisplayName,
  [PaymentInstrumentType.Invoice]: getInvoiceDisplayName,
  [PaymentInstrumentType.Check]: getCheckDisplayName,
  [PaymentInstrumentType.ElectronicFundsTransfer]: getEFTDisplayName,
  [PaymentInstrumentType.PayPal]: getPayPalDisplayName,
  [PaymentInstrumentType.ELV]: getSepaDisplayName,
  [PaymentInstrumentType.OfflinePaymentMethod]: getOfflineDisplayName,
  [PaymentInstrumentType.Boleto]: getBoletoDisplayName,
  [PaymentInstrumentType.VBA]: getVBADisplayName,
};

export const isExpired = (pi?: PaymentInstrument) => {
  if (!(pi && Date.parse(pi.ExpirationDate))) {
    return false;
  }
  const date = new Date(pi.ExpirationDate);
  return date.setMonth(date.getMonth() + 1) <= (new Date() as Date & number);
};

export const isOnHold = (pi?: PaymentInstrument) => _.isObject(pi)
  && pi.FinancialStatus !== null
  && pi.FinancialStatus !== undefined
  && pi.FinancialStatus !== PaymentInstrFinancialStatus.NoHold;

export const isDeleted = (pi?: PaymentInstrument) => _.isObject(pi) && pi.LifecycleStatusId === PaymentInstrLifecycleStatus.Deleted;

export const isPurged = (pi?: PaymentInstrument) => _.isObject(pi) && pi.IsPurged === true;

export const isIndiaPI = (pi?: PaymentInstrument) => _.isObject(pi) && pi.Address && pi.Address.Country === CountryCode.IN;

/**
 * Gets a string representation of the expiration date in 'mm/yy' format
 * @param {string} dateString The date string
 * @returns {string} the string 'mm/yy' format of the date
*/
export const formatExpDate = (dateString?: string) => {
  if (!Date.parse(dateString)) {
    return '';
  }

  const date = new Date(dateString);
  const yearString = date.getFullYear().toString().substr(-2);
  const monthString = `0${date.getMonth() + 1}`.substr(-2);
  return `${monthString}/${yearString}`;
};

export const getDisplayName = (pi?: PaymentInstrument, i18n?: PaymentI18n) => {
  if (!(pi && pi.Id && i18n && i18n.getString)) {
    return '';
  }

  const getDisplayNameFunc = getDisplayNameDict[pi.Type as keyof typeof getDisplayNameDict];
  if (getDisplayNameFunc) {
    return getDisplayNameFunc(pi, i18n);
  }

  return i18n.getString(_TL_('Payment method: {{id}}'), { id: pi.Id });
};

/**
 * Gets the display string of card status, whether it's on hold, expired or active
 * @param {object} pi the payment instrument
 * @param {object} i18n the i18n object
 * @param {object} permissions the permissions object
 * @returns {string} display string of the payment instrument of type CreditCard
 */
export const getCardStatusDisplay = (pi?: PaymentInstrument, i18n?: PaymentI18n) => {
  if (!(pi && i18n && i18n.getString && i18n.formatDate)) {
    return '';
  }

  if (isOnHold(pi)) {
    const date = Date.parse(pi.OnHoldDate) ? i18n.formatDate(new Date(pi.OnHoldDate)) : '';
    return i18n.getString(_TL_('On hold {{date}}'), { date });
  }

  return i18n.getString(
    isExpired(pi) ? _TL_('Expired {{date}}') : _TL_('Expires {{date}}'),
    { date: isIndiaPI(pi) ? i18n.getString(_TL_('N/A')) : formatExpDate(pi.ExpirationDate) }
  );
};

/**
 * Get the payment option text by the given paymentOptionId
 * @param {number} paymentOptionId the payment option Id
 * @param {*} i18n the i18n object
 * @returns {string} display text of the payment option
 */
export const getPaymentOptionTextById = (paymentOptionId?: number, i18n?: PaymentI18n) => {
  const paymentOptionIds = _.values(PaymentOptionId);
  return _.contains(paymentOptionIds, paymentOptionId) ?
    i18n.getString(PaymentOptionString[paymentOptionId as keyof typeof PaymentOptionString]) :
    undefined;
};

/**
 * Get the payment method formatted display string based on MT returned fields
 * This is the same function of server side 'BillingHelper.GetPaymentMethodFormatted'
 * @param {object} piInformation contains below properties:
 *  paymentInstrumentTypeId indicate this PI's type Id
 *  paymentInstrAccountNumber contains this payment's account information, for different payment type this value is different.
 *  paymentInstrFriendlyName this holds the PI friendly named used by Paypal, VBA and Boleto.
 *  paymentInstrCardTypeId this holds the Credit Card Type Id if the PI type is Credit Card.
* @param {*} i18n the i18n object
 * @returns {string} display text of the formatted payment method
 */
export const getPaymentMethodFormatted = (
  {
    paymentInstrumentTypeId,
    paymentInstrAccountNumber,
    paymentInstrFriendlyName,
    paymentInstrCardTypeId,
  }: PaymentMethodInformation,
  i18n?: PaymentI18n
) => {
  let paymentMethodText;
  if (paymentInstrumentTypeId && paymentInstrumentTypeId !== PaymentInstrumentType.Unset) {
    switch (paymentInstrumentTypeId) {
      case PaymentInstrumentType.CreditCard: {
        if (paymentInstrCardTypeId) {
          paymentMethodText = getCreditCardDisplayName({
            CardTypeId: paymentInstrCardTypeId,
            Number: paymentInstrAccountNumber,
          }, i18n);
        }
        break;
      }
      case PaymentInstrumentType.Invoice: {
        paymentMethodText = getInvoiceDisplayName({ InvoiceId: paymentInstrAccountNumber }, i18n);
        break;
      }
      case PaymentInstrumentType.PayPal: {
        paymentMethodText = getPayPalDisplayName({ Email: paymentInstrFriendlyName }, i18n);
        break;
      }
      case PaymentInstrumentType.ELV: {
        paymentMethodText = getSepaDisplayName({ FriendlyName: paymentInstrAccountNumber }, i18n);
        break;
      }
      case PaymentInstrumentType.OfflinePaymentMethod: {
        paymentMethodText = getOfflineDisplayName({}, i18n);
        break;
      }
      case PaymentInstrumentType.VBA: {
        paymentMethodText = getVBADisplayName({ FriendlyName: paymentInstrFriendlyName }, i18n);
        break;
      }
      case PaymentInstrumentType.Boleto: {
        paymentMethodText = getBoletoDisplayName({ FriendlyName: paymentInstrFriendlyName }, i18n);
        break;
      }
      default:
        break;
    }
  }
  return paymentMethodText;
};

/**
 * Calculates the remaining credit for a given SAP, returning null if there is no applicable limit.
 * @param {object} sap the Invoice object to check
 * @param {object} options Some optional parameter to pass, like matchCurrencyCode to ensure that the SAP object matches the given currency code
 * @returns {number} the remaining credit for the given SAP or null if not applicable
 */
export const sapCreditRemaining = (sap?: PaymentInstrument, options: { matchCurrencyCode?: string } = {}) => {
  const { matchCurrencyCode } = options;
  const hasValidCreditLimit =
    _.isObject(sap) &&
    sap.Type === PaymentInstrumentType.Invoice &&
    sap.IsCLEOn === true &&
    sap.CreditLimit > 0 &&
    (!matchCurrencyCode || (sap.CurrencyCode || '').toLowerCase() === matchCurrencyCode.toLowerCase());

  return hasValidCreditLimit ?
    sap.CreditLimit - (sap.SAPBalance || 0) :
    null;
};

/**
 * Calculates the projected number of days remaining before the given SAP runs out of credit,
 * returning null if there is no valid estimate.
 * @param {object} sap the Invoice object to check
 * @returns {number} the number of days remaining before the given SAP runs out of credit or null
 *                   if there is no valid estimate
 */
export const sapCreditDaysRemaining = (sap?: PaymentInstrument) => {
  const creditRemaining = sapCreditRemaining(sap);

  return creditRemaining != null && sap.SAPSpendRunRate > 0 ?
    Math.ceil(creditRemaining / sap.SAPSpendRunRate) :
    null;
};

/**
 * Returns the status whether the passed pi was validated
 * @param {object} pi the payment instrument
 * @returns {bool} indicate whether the pi had already validated
 */
export const isPiValidated = (pi?: PaymentInstrument) => _.isObject(pi) && Number(pi.LifecycleStatusId) > 0 && pi.LifecycleStatusId !== PaymentInstrLifecycleStatus.PendingValidation;

const isPiType = (pi?: PaymentInstrument, piType?: number) => _.isObject(pi) && piType && pi.Type === piType;

/**
 * Return whether the payment instrument is the indicated type of pi.
 * @param {object} pi the PaymentInstrument object
 * @returns {bool} indicate whether the provided Pi is the indicated type of pi.
 */
export const isCreditCard = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.CreditCard);

export const isInvoice = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.Invoice);

export const isBoleto = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.Boleto);

export const isOfflinePaymentMethod = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.OfflinePaymentMethod);

export const isSepa = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.ELV);

export const isPaypal = (pi?: PaymentInstrument) => isPiType(pi, PaymentInstrumentType.PayPal);

export const isPaasPI = (pi?: PaymentInstrument) => _.isObject(pi) && pi.PaymentInstrSource === 'PAAS';

export const isNukePIInvalid = (pi?: PaymentInstrument) => isCreditCard(pi) && (pi.NukedPIStatus === NukedPIStatusTypes.Invalid || pi.NukedPIStatus === NukedPIStatusTypes.AltInvalid);

export const isNukePIShowButton = (pi?: PaymentInstrument) => isCreditCard(pi) && pi.NukedPIStatus === NukedPIStatusTypes.ValidShowButton;

const getPiCountry = (pi: PaymentInstrument) => pi.Address ? pi.Address.Country : null;

/**
 * Check whether the given backup PI can be set as a backup for the given primary PI.
 * @param {Object} obj.backupPi - PI to check whether it can be set as backup
 * @param {Object} obj.primaryPi - PI set currently set as primary PI
 * @returns {Boolean} - A value indicating whether the PI can be set as backup
 */
export const isValidBackup = ({
  backupPi,
  primaryPi,
}: { backupPi: PaymentInstrument; primaryPi: PaymentInstrument }) =>
  backupPi.SupportsRecurringPayment &&
  backupPi.Id !== primaryPi.Id &&
  backupPi.IsTaxable === primaryPi.IsTaxable &&
  (!primaryPi.IsTaxable || getPiCountry(backupPi) === getPiCountry(primaryPi));

/**
 * Returns a copy of the given PaymentInstrument object with additional utility methods.
 * @param {object} pi The PaymentInstrument object to be extended with additional utility methods
 * @param {object} i18n The i18n object
 * @param {object} permissions The permissions object
 * @returns {object} A copy of the extended PaymentInstrument object.
 */
export const extendPi = (pi: PaymentInstrument = {}, i18n?: PaymentI18n, permissions: object = {}) => _.defaults({}, pi, {
  id: pi && pi.Id, // react DropdownSelector expects an id property
  displayName: getDisplayName(pi, i18n),
  cardStatus: (pi && pi.Type) === PaymentInstrumentType.CreditCard ?
    (getCardStatusDisplay as typeof getCardStatusDisplay & ((pi: PaymentInstrument, i18n: PaymentI18n, permissions: object) => string))(pi, i18n, permissions) :
    '',
  isDeleted: isDeleted(pi),
  isOnHold: isOnHold(pi),
  isPurged: isPurged(pi),
  isExpired: isExpired(pi),
  isValidated: isPiValidated(pi),
  isInvoice: isInvoice(pi),
  isBoleto: isBoleto(pi),
  isOfflinePaymentMethod: isOfflinePaymentMethod(pi),
  isCreditCard: isCreditCard(pi),
  isSepa: isSepa(pi),
  isPaypal: isPaypal(pi),
  isNukePIInvalid: isNukePIInvalid(pi),
  isNukePIShowButton: isNukePIShowButton(pi),
});

/**
 * Returns a sorted list of PaymentInstrument ordered by Active > Expired > OnHold, then by Type, then by name.
 * @param {object[]} piList A list of PaymentInstrument object to be extended and sorted.
 * @param {object} i18n The i18n object
 * @param {object} permissions The permissions object
 * @returns {object[]} The sorted list of extended PaymentInstrument object.
 */
export const sortPiList = (piList: PaymentInstrument[] = [], i18n?: PaymentI18n, permissions: object = {}) => {
  if (_.isEmpty(piList)) {
    return [];
  }

  const piListExtended = _.map(piList, (pi: PaymentInstrument) => extendPi(pi, i18n, permissions));
  const activePis = _.chain(piListExtended)
    .reject(pi => pi.isOnHold || pi.isExpired)
    .sortBy('displayName')
    .sortBy('Type')
    .value();
  const expiredPis = _.chain(piListExtended)
    .filter(pi => pi.isExpired && !pi.isOnHold)
    .sortBy('displayName')
    .value();
  const onHoldPis = _.chain(piListExtended)
    .filter(pi => pi.isOnHold)
    .sortBy('displayName')
    .value();
  return _.union(activePis, expiredPis, onHoldPis);
};

/**
 * Returns a string enum value for Payment Type
 * @param {PaymentTypeId} piTypeId The payment type Id.
 * @returns {PaymentType} The payment type.
 */
export const convertPaymentTypeToString = (piTypeId?: number) => {
  const key = _.findKey(PaymentTypeId, (val: number) => val === piTypeId);
  return PaymentType[key as keyof typeof PaymentType];
};

/**
 * Returns a string enum value for Payment Type
 * @param {PaymentType} piType The payment type.
 * @returns {PaymentTypeId} The payment type id.
 */
export const convertPaymentTypeToInt = (piType?: string) => {
  const key = _.findKey(PaymentType, (val: string) => val === piType);
  return PaymentTypeId[key as keyof typeof PaymentTypeId];
};

/**
 * Returns a string enum value for Payment Option
 * @function
 * @param {PaymentOptionId} paymentOptionId The payment option Id.
 * @returns {string} The payment option as a string value.
 */
export const convertPaymentOptionToString = (paymentOptionId?: number) => _.result(PaymentOption, _.findKey(PaymentOptionId, (val: number) => val === paymentOptionId), null);

/**
 * Returns a number enum value for Payment Option
 * @function
 * @param {PaymentOption} paymentOptionString The payment option string.
 * @returns {number} The payment option as a number value.
 */
export const convertPaymentOptionToId = (paymentOptionString?: string) => _.result(PaymentOptionId, _.findKey(PaymentOption, (val: string) => val === paymentOptionString), null);

/**
 * Returns the payment type corresponding to the given payment option ID.
 * @param {Number} paymentOptionId - payment option ID to lookup
 * @returns {string} - payment type corresponding to the given payment option ID
 */
export const getPaymentTypeForPaymentOption = (paymentOptionId?: number) => _.findKey(PaymentSettingsMap, (p: readonly number[]) => _.contains(p, paymentOptionId)) || null;

/**
 * Return the text key of the payment transaction status used by billing grid.
 * @param {PaymentTransactionStatus} paymentTransactionStatus the input payment transaction status
 * @param {bool} hasPaymodError whether has paymod error
 * @returns {string} the text key of the payment transaction status used by billing grid.
 */
export const getPaymentTransactionStatus = (paymentTransactionStatus?: number, hasPaymodError?: boolean) => {
  if (paymentTransactionStatus
    && paymentTransactionStatus !== PaymentTransactionStatus.Unknown
    && paymentTransactionStatus !== PaymentTransactionStatus.Success
    && !hasPaymodError) {
    return PaymentStatusI18nMap[paymentTransactionStatus as keyof typeof PaymentStatusI18nMap];
  }
  return null;
};

/**
 * Return a filtered list of newly added pending SAPApplications
 * @param {object[]} [sapApplications=[]] the original SAPApplication list to filter
 * @returns {object[]} the filtered SAPApplication list
 */
export const filterPendingSAPApplicationsOfNewSAP = (sapApplications: SAPApplication[] = []) => {
  if (!_.isArray(sapApplications)) {
    return [];
  }

  const types = [SAPApplicationType.ApplyNewBilltoSAP, SAPApplicationType.ApplyNewSoldtoSAP, SAPApplicationType.AddingExistingSAP];
  const statuses = [SAPApplicationStatus.UnderReview, SAPApplicationStatus.ActionRequired];

  return _.filter(sapApplications, ({ SAPApplicationType: sapApplicationType, SAPApplicationStatus: sapApplicationStatus }: SAPApplication) =>
    _.contains(types, sapApplicationType) && _.contains(statuses, sapApplicationStatus));
};

const onlinePiTypes = [
  PaymentInstrumentType.CreditCard,
  PaymentInstrumentType.PayPal,
];

const offlinePiTypes = [
  PaymentInstrumentType.Boleto,
  PaymentInstrumentType.ELV,
  PaymentInstrumentType.OfflinePaymentMethod,
];

/**
 * Return a list of payment instruments filtered by payment option
 * @param {object[]} paymentInstruments the list of PI's to filter
 * @param {number} paymentOptionId The type of payment option to use as filter criteria
 * @returns {object[]} the filtered list
 */
export const filterPisByPaymentOption = (paymentInstruments?: PaymentInstrument[], paymentOptionId?: number) => {
  switch (paymentOptionId) {
    case PaymentOptionId.Invoice:
      return _.filter(
        paymentInstruments,
        (paymentInstrument: PaymentInstrument) => paymentInstrument.Type === PaymentInstrumentType.Invoice
      );

    case PaymentOptionId.Prepay:
      return _.filter(
        paymentInstruments,
        (paymentInstrument: PaymentInstrument) => _.contains([...onlinePiTypes, ...offlinePiTypes], paymentInstrument.Type)
      );

    case PaymentOptionId.Threshold:
      return _.filter(
        paymentInstruments,
        (paymentInstrument: PaymentInstrument) => _.contains(
          onlinePiTypes,
          paymentInstrument.Type
        ) && paymentInstrument.SupportsPostpay
      );

    default:
      return [];
  }
};

/**
 * Filters the specified payment instruments,
 * based on their compatibility with the specified account mode.
 * @param {object[]} paymentInstruments the list of payment instruments to be checked for compatibility
 * @param {number} paymentInstruments[].Type the type of payment instrument, e.g. credit card, etc.
 * @param {number|null} accountMode the account mode which all specified payment instruments need to be compatible with
 * @returns {object[]} the filtered list of payment instruments
 */
export const filterPaymentInstrumentsByAccountMode = (paymentInstruments?: PaymentInstrument[], accountMode?: number | string | null) =>
  (accountMode !== AccountMode.UnifiedSmart
    ? paymentInstruments
    : _.filter(paymentInstruments, (pi: PaymentInstrument) => !_.contains(ESCPrepayUnsupportedPITypes, pi.Type)));

export const getPaymentReason = (documentType?: number | string) => {
  if (documentType === DocumentType.WriteOffCreditMemo) {
    return _TL_('Adjustment for uncollected amount');
  }

  return null;
};

/**
 * Filter payment options by user's account mode
 * @param {Object[]} paymentOptions Payment options returned by backend
 * @param {string | null} accountMode account mode
 * @param {Object} pilots Permissions/Features
 * @param {function} poKeySelector An optional function that selects
 * the payment option ID from elements of {@link paymentOptions} collection.
 * By default the elements are assumed to be the IDs.
 * @returns {Object[]} Filtered payment options, share the same type with paymentOptions param
 */
export const filterPaymentOptionsByAccountMode = <Option>(
  paymentOptions?: Option[],
  accountMode?: number | string | null,
  pilots?: PaymentPilots,
  poKeySelector?: (option: Option) => number | string
) => {
  const keySelector = poKeySelector || ((option: Option) => option);
  const {
    UnifiedProductMonthlyInvoice: isUnifiedProductMonthlyInvoiceEnabled,
    ESCPrepay: isEscPrepayEnabled,
  } = pilots || {};

  return accountMode !== AccountMode.UnifiedSmart
    ? paymentOptions
    : _.filter(paymentOptions, (option: Option) => {
      switch (`${keySelector(option)}`) {
        case `${PaymentOptionId.Invoice}`:
          return isUnifiedProductMonthlyInvoiceEnabled;
        case `${PaymentOptionId.Prepay}`:
          return isEscPrepayEnabled;
        case `${PaymentOptionId.Threshold}`:
          return true;
        default:
          return false;
      }
    });
};
